import { clearStorage, getAuthToken } from "../../helpers";
import { BellIcon, UserIcon } from "../../assets/svg";
import { useLocation, Link, useNavigate, useParams } from "react-router";
import { useUserStore } from "../../zustand/auth";
import TrophyProgress from "../Progress";
import { useEffect, useState } from "react";
import { fetchCourseProgress } from "../../services";

const routes = [
  { name: "Dashboard", path: "/dashboard" },
  { name: "My Learning", path: "/my-learning" },
  { name: "Settings", path: "/settings" },
];

export const Navbar = () => {
  const navigate = useNavigate();
  const { courseName, id } = useParams();
  const { pathname } = useLocation();
  const { userData, updatePercentage } = useUserStore();
  const [percentage, setPercentage] = useState(0);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const getProgress = async () => {
      try {
        const res = await fetchCourseProgress(Number(id));
        setPercentage(res?.data?.percentage);
        updatePercentage(res?.data?.percentage);
      } catch (err) { }
    };
    if (courseName) getProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseName, id]);

  // ── Course-view navbar ──────────────────────────────────────────────────
  if (courseName) {
    return (
      <header className="sticky top-0 z-50 bg-[#244557] border-b border-white/5 shadow-xl shadow-primary-950/20">
        <div className="flex items-center justify-between px-6 md:px-11 h-[68px] max-w-[1440px] mx-auto">
          <div className="flex items-center gap-5">
            <Link to="/" className="font-display font-bold text-white text-lg md:text-xl tracking-tight whitespace-nowrap hover:text-[#369494] transition-colors">
              BudgIT Academy
            </Link>
            <div className="hidden sm:block h-5 w-px bg-white/10" />
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-[10px] font-brand font-black uppercase tracking-[0.2em] text-white/30">Learning Portal</span>
              <span className="text-white/10 text-xs">/</span>
              <p className="text-xs text-[#369494] font-brand font-bold uppercase tracking-widest truncate max-w-[200px] leading-none">
                {courseName.replace(/-/g, ' ')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex flex-col items-end">
              <p className="text-white/40 text-[9px] font-brand font-black tracking-[0.2em] uppercase leading-none mb-1.5">Module Progress</p>
              <div className="flex items-center gap-3">
                <TrophyProgress value={percentage} />
                <span className="text-xs font-brand font-black text-white/90">{percentage}%</span>
              </div>
            </div>

            <div className="h-8 w-px bg-white/10 mx-1 hidden md:block" />

            <button
              onClick={() => navigate("/my-learning")}
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white transition-all group"
            >
              <span className="text-[10px] font-brand font-black uppercase tracking-widest">Exit Portal</span>
              <svg className="w-3.5 h-3.5 text-white/40 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>
    );
  }

  // ── Standard navbar ─────────────────────────────────────────────────────
  return (
    <header
      className={`sticky top-0 z-50 bg-white transition-all duration-300 ${scrolled ? "shadow-2xl shadow-primary-900/5" : "border-b border-slate-200"
        }`}
    >
      {/* --- Top Tier: Logo & User Actions --- */}
      <div className="flex items-center justify-between px-6 md:px-11 h-[68px] max-w-[1440px] mx-auto relative z-50">
        <Link to="/" className="font-display font-bold text-primary-900 text-base md:text-xl whitespace-nowrap tracking-tight hover:opacity-80 transition-opacity">
          BudgIT Academy
        </Link>

        <div className="flex items-center gap-4 md:gap-6">
          <button className="relative text-slate-500 hover:text-primary-900 transition-colors">
            <BellIcon />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-lochinvar rounded-full border-2 border-white" />
          </button>

          {getAuthToken() ? (
            userData && (
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="w-9 h-9 rounded-full bg-primary-100 border-2 border-primary-200 flex items-center justify-center hover:border-primary-400 transition-colors"
                >
                  <UserIcon />
                </button>
                {showUserDropdown && (
                  <div className="absolute top-11 right-0 w-64 flex-col rounded-3xl shadow-2xl shadow-primary-900/10 bg-white border border-slate-100 overflow-hidden z-50 flex animate-fade-up">
                    <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                      <p className="text-xs font-brand font-black uppercase tracking-[0.2em] text-lochinvar mb-1">Authenticated</p>
                      <p className="text-sm font-brand font-black text-primary-950">{userData?.first_name} {userData?.last_name}</p>
                    </div>
                    <div className="p-2 flex flex-col gap-1">
                      {routes.slice(1).map((r) => (
                        <Link
                          key={r.path}
                          to={r.path}
                          className="px-4 py-3 rounded-xl text-xs font-brand font-bold uppercase tracking-widest text-slate-600 hover:bg-slate-50 hover:text-primary-950 transition-colors"
                        >
                          {r.name}
                        </Link>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        clearStorage();
                        window.location.reload();
                      }}
                      className="m-2 px-4 py-3 rounded-xl text-xs font-brand font-black uppercase tracking-widest text-left text-danger hover:bg-red-50 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/login")}
                className="hidden sm:block h-10 px-5 rounded-xl font-brand font-bold text-slate-500 hover:text-primary-950 transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate("/login")}
                className="h-10 px-5 md:px-7 rounded-xl bg-primary-950 font-brand font-bold text-white text-xs uppercase tracking-widest hover:bg-lochinvar transition-all shadow-lg shadow-primary-900/20"
              >
                Get Started
              </button>
            </div>
          )}

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-primary-950"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={showMobileMenu ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>
      </div>

      {/* --- Bottom Tier: Navigation Tabs --- */}
      {getAuthToken() && (
        <div className={`${showMobileMenu ? 'flex' : 'hidden'} md:flex border-t border-slate-100 bg-slate-50/50 backdrop-blur-sm`}>
          <div className="flex items-center justify-between px-6 md:px-11 max-w-[1440px] mx-auto w-full">
            <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar whitespace-nowrap -mb-px">
              {routes.map((r) => (
                <Link
                  key={r.path}
                  to={r.path}
                  className={`relative px-4 py-3.5 text-sm font-semibold transition-all ${pathname === r.path
                    ? "text-[#244557] border-b-2 border-[#244557]"
                    : "text-slate-500 hover:text-[#244557]"
                    }`}
                >
                  {r.name}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};
